import { ChartContainer, type ChartConfig } from '@/components/ui-kit/chart';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useToolSummary } from '@/modules/gpt-chats/hooks/use-tools';
import { IToolSummaryPayload } from '@/modules/gpt-chats/types/tools.service.type';
import { useMemo, useEffect } from 'react';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { parseBackendUtc } from '@/modules/gpt-chats/utils/date-utils';
import { useTranslation } from 'react-i18next';
export const ToolDashboardChart = ({
  toolSummaryPayload,
  period,
  setTotalCalls,
  setSuccessfulCalls,
  setFailedCalls,
}: {
  toolSummaryPayload: IToolSummaryPayload;
  period: string;
  setTotalCalls: (totalCalls: number) => void;
  setSuccessfulCalls: (successfulCalls: number) => void;
  setFailedCalls: (failedCalls: number) => void;
}) => {
  const { isLoading, data: toolSummary } = useToolSummary(toolSummaryPayload);
  const { t } = useTranslation();
  useEffect(() => {
    if (!isLoading) {
      setTotalCalls(toolSummary?.data?.total_calls || 0);
      setSuccessfulCalls(toolSummary?.data?.successful_calls || 0);
      setFailedCalls(toolSummary?.data?.failed_calls || 0);
    }
  }, [isLoading, toolSummary, setTotalCalls, setSuccessfulCalls, setFailedCalls]);
  const chartConfig = {
    requests: {
      label: t('REQUESTS'),
      color: 'hsl(var(--primary))',
    },
    rate: {
      label: t('SUCCESS_RATE'),
      color: 'hsl(142.1 76.2% 36.3%)',
    },
  } satisfies ChartConfig;
  const { requestsData } = useMemo(() => {
    const logs = toolSummary?.data?.logs || [];
    if (period === '24h') {
      const buckets = Array.from({ length: 24 }, (_, i) => ({
        duration: `${i.toString().padStart(2, '0')}:00`,
        requests: 0,
      }));
      logs.forEach((log) => {
        const date = parseBackendUtc(log.created_at);
        const duration = date.getHours();
        if (duration >= 0 && duration < 24) {
          buckets[duration].requests++;
        }
      });
      return {
        requestsData: buckets.map((b) => ({ duration: b.duration, requests: b.requests })),
      };
    } else {
      const days = period === '7d' ? 7 : 30;
      const buckets = Array.from({ length: days }, (_, i) => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - (days - 1 - i));
        return {
          fullDate: d.toDateString(),
          date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          requests: 0,
        };
      });
      const bucketMap = new Map();
      buckets.forEach((b) => bucketMap.set(b.fullDate, b));
      logs.forEach((log) => {
        const logDate = parseBackendUtc(log.created_at).toDateString();
        const bucket = bucketMap.get(logDate);
        if (bucket) {
          bucket.requests++;
        }
      });
      return {
        requestsData: buckets.map((b) => ({ duration: b.date, requests: b.requests })),
      };
    }
  }, [toolSummary, period]);
  if (isLoading) {
    return (
      <div className="max-h-[320px] w-full overflow-x-auto pb-2">
        <div className="flex h-full min-w-[600px] items-end gap-2">
          {Array.from({ length: 24 }).map((_, index) => (
            <Skeleton
              key={index}
              className="w-full rounded-t-md"
              style={{
                height: `${[35, 60, 45, 75, 50, 80, 55, 90, 65, 40, 70, 30][index % 12]}%`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="h-full w-full overflow-x-auto">
      <div className="h-full min-w-[600px]">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <BarChart data={requestsData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="duration"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval="preserveStartEnd"
              allowDecimals={false}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              domain={[0, 'dataMax']}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted))' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col">
                          <span className="text-[0.70rem] uppercase text-muted-foreground">
                            {t('REQUESTS')}
                          </span>
                          <span className="font-bold text-muted-foreground">
                            {payload[0].value}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="requests" fill="var(--color-requests)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  );
};
